import type { ReadonlyRecord } from './shared/util';

export interface TrainerSummary {
    readonly id: string;
    readonly cheatCount: number;
    readonly supportedVersions: readonly number[];
    readonly releasedAt: string;
    readonly createdAt: string;
}

export interface TrainerResponse {
    readonly pinnedMods: readonly string[];
    readonly trainer: TrainerInfo;
    readonly i18n: TrainerI18n;
}

export interface TrainerI18n {
    readonly locale: string;
    readonly strings: ReadonlyRecord<string, string>;
}

export interface TrainerInfo {
    readonly id: string;
    readonly gameId: string;
    readonly titleId: string;
    readonly releasedAt: string;
    readonly loader: TrainerLoader;
    readonly loaderArgs: TrainerLoaderArgs;
    readonly supportedVersions: readonly number[];
    readonly blueprint: TrainerBlueprint;
    readonly brokenCheatUuids: readonly string[] | null;
    readonly pinnedMods: string[];
    readonly gameEvents: readonly TrainerGameEvent[];
}

export type TrainerLoader = 'trainerlib' | 'trainerlib-local' | 'artifacts' | (string & {});

export interface TrainerLibLoaderArgs {
    readonly trainerId: string;
    readonly binaryUrl: string;
    readonly binaryHash: string;
}

export interface TrainerLibLocalLoaderArgs {
    readonly fileSafeTitleName: string;
    readonly blueprintEditUrl: string;
    readonly repoPath: string;
}

export interface TrainerLibArtifact extends TrainerLibLoaderArgs {
    readonly type: 'trainerlib';
}

export interface TrainerLuaArtifact {
    readonly type: 'lua';
    readonly id: string;
    readonly artifactHash: string;
    readonly contract: string;
    readonly contractService?: string;
    readonly contractDescriptor?: string;
    readonly contractDescriptorHash?: string;
}

export interface TrainerArtifactsLoaderArgs {
    readonly artifacts: readonly (TrainerLibArtifact | TrainerLuaArtifact)[];
}

export type TrainerLoaderArgs = TrainerLibLoaderArgs | TrainerLibLocalLoaderArgs | TrainerArtifactsLoaderArgs | object;

export interface TrainerInfoRemote {
    readonly id: string;
    readonly title: TitleInfoRemote;
    readonly blueprint: TrainerBlueprint;
    readonly i18n: TrainerI18n;
    readonly game: TrainerGameRemote;
}

export interface TrainerGameRemote {
    readonly id: string;
    readonly platformId: string;
}

export interface TitleInfoRemote {
    readonly id: string;
    readonly name: string;
    readonly thumbnail: string;
    readonly steamAppId: number | null;
}

export interface TrainerBlueprint {
    readonly cheats: readonly CheatBlueprint[];
    readonly notes?: string;
    readonly flags?: TrainerFlags;
    readonly config?: TrainerConfig;
    readonly events?: readonly string[];
}

export enum TrainerFlags {
    SupportsActivateOnLoad = 1 << 0,
    ShowGameMaps = 1 << 1,
    SupportsTeleporting = 1 << 2,
    SupportsLocation = 1 << 3,
    HasLuaMods = 1 << 4,
}

export interface TrainerStatus {
    readonly isRunning: boolean;
    readonly flags: TrainerFlags | 0;
}

export interface TrainerConfig {
    readonly launch?: false | AppLaunchConfig;
    readonly target?: ProcessTargetConfig;
    readonly activate?: {
        readonly flags?: TrainerActivationFlags;
    };
}

export interface AppLaunchConfig {
    readonly required?: true;
    readonly cliArgs?: string;
    readonly proxy?: {
        readonly launcher: string;
        readonly gameExe: string;
        readonly gameArgs?: string;
        readonly gameCwd?: string;
        readonly flags?: number;
    };
    readonly bypass?: {
        readonly launcher: string;
        readonly gameExe: string;
        readonly gameArgs?: string;
        readonly patchscan?: string;
        readonly patchReplace?: string;
    };
}

export interface AppLaunchPatch {
    readonly scan: string;
    readonly replace: string;
}

export interface ProcessTargetConfig {
    readonly timeout?: number;
    readonly minimumAge?: number;
    readonly image?: string;
    readonly cliArgs?: string;
    readonly reducer?: ProcessReductionStrategy;
}

export enum TrainerActivationFlags {
    SuspendProcess = 1 << 0,
    ResumeProcess = 1 << 1,
    HideThreads = 1 << 2,
    MaskModules = 1 << 3,
    UseExternalHost = 1 << 4,
}

export enum ProcessReductionStrategy {
    MostMemory = 0,
    Oldest = 1,
    Newest = 2,
}

export interface CheatBlueprint {
    readonly uuid: string;
    readonly category: CheatCategory;
    readonly name: string;
    readonly description?: string;
    readonly instructions?: string;
    readonly type: CheatType;
    readonly args?: CheatArgs;
    readonly target: string;
    readonly contractBinding?: LuaContractBinding;
    readonly hotkeys: readonly number[][];
    readonly flags: CheatFlags;
    readonly parent?: string | null;
}

export interface LuaContractBinding {
    readonly contract: string;
    readonly method: string;
    readonly argField: string;
}

export enum CheatFlags {
    SupportsActivateOnLoad = 1 << 0,
    Beta = 1 << 1,
    Challenge = 1 << 2,
}

export type CheatCategory =
    | 'player'
    | 'weapons'
    | 'inventory'
    | 'stats'
    | 'vehicles'
    | 'enemies'
    | 'physics'
    | 'teleport'
    | 'game'
    | 'pinned'
    | 'challenge';
export type CheatType = 'toggle' | 'button' | 'number' | 'slider' | 'selection' | 'scalar' | 'incremental';
export type CheatArgs = ButtonCheatArgs &
    NumberCheatArgs &
    SliderCheatArgs &
    SelectionCheatArgs &
    ScalarCheatArgs &
    IncrementalCheatArgs;

export interface ButtonCheatArgs {
    readonly value: number;
}

export interface NumberCheatArgs {
    readonly min: number;
    readonly max: number;
    readonly step: number;
    readonly slider: boolean;
    readonly button?: 'set' | 'add';
    readonly default?: number | string;
}

export interface SliderCheatArgs {
    readonly min: number;
    readonly max: number;
    readonly step: number;
    readonly slider: boolean;
    readonly default?: number | string;
}

export interface SelectionCheatArgs {
    readonly options: readonly (string | number)[];
    readonly button?: 'set';
}

export interface ScalarCheatArgs {
    readonly options: readonly number[];
    readonly postfix: string;
    readonly default?: number | string; // string for backwards compatibility
}

export interface IncrementalCheatArgs {
    readonly options: readonly number[];
}

export enum TrainerEventType {
    PlayPressed = 0,
    GameLaunched = 1,
    GameFound = 2,
    TrainerExecuted = 3,
    TrainerInitialized = 4,
    ValueChanged = 5,
    LogMessage = 6,
    TrainerEnd = 7,
    TrainerInjecting = 8,
    OpeningConnection = 9,
}

export interface TrainerEvent {
    type: TrainerEventType;
    time: number;
    data?: TrainerEventData;
}

export type TrainerEventData = GameFoundEventData | ValueChangedEventData | LogMessageEventData | GameEvent;

export interface GameEvent {
    readonly name: string;
    readonly value: number;
}

export interface GameFoundEventData {
    process_name: string;
}

export type ValueSource = 'app' | 'trainer' | 'remote' | 'overlay' | 'save_cheats';

export interface ValueChangedEventData {
    name: string;
    source: ValueSource;
    old_value: number;
    new_value: number;
}

export interface LogMessageEventData {
    level: number;
    message: string;
}

export type TrainerReport = TrainerSuccessReport | TrainerFailureReport;

export interface TrainerSuccessReport {
    feedback: string | null;
    allowPublic: boolean;
}

export interface TrainerFailureReport {
    crashLocation: GameCrashLocation | null;
    allCheatsBroken: boolean | null;
    cheats: CheatIssue[];
    notes: string | null;
}

export enum GameCrashLocation {
    Other = 0,
    MainMenu = 1,
    Loading = 2,
    InGamePlaying = 3,
    InGameMenu = 4,
}

export enum CheatIssueType {
    Other = 0,
    GameCrash = 1,
    NoEffect = 2,
    NotAsExpected = 3,
}

export interface CheatIssue {
    uuid: string;
    type: CheatIssueType | undefined;
    notes: string | null;
}

export enum TrainerReportType {
    Success = 0,
    Failure = 1,
}

export interface TrainerFeedback {
    trainerId: string;
    correlationId: string | null;
    gameVersion: number | null;
    type: TrainerReportType;
    report: TrainerReport | null;
    events: TrainerEvent[] | null;
    system: {
        version: string;
        locale: string;
        arch: 'x64' | 'ia32';
        freeMemory: number;
        antivirus: string[];
    };
    app: {
        version: string;
    };
}

export interface TrainerBackendResolution {
    readonly backend: 'tophat' | 'trainerlib';
    readonly experimentVariant: number | null;
    readonly artifactsEnabled?: boolean;
    readonly disabledArtifactIds?: readonly string[];
}

export const DEFAULT_GAME_EVENT_CATEGORY_KEY = 'general';

interface TrainerGameEventAutoClipConfig {
    readonly isActive?: boolean;
    readonly offsetInSeconds?: number;
}

export interface TrainerGameEvent {
    readonly key: string;
    readonly name: string;
    readonly category: string;
    readonly autoClipConfig?: TrainerGameEventAutoClipConfig;
}
